import { useTranslation } from "react-i18next";
import {
  formatInTimezone,
  getTimezoneAbbreviation,
  DEFAULT_TIMEZONE,
} from "@shared/timezone-utils";
import { resolveStopTimezone } from "@shared/utils/timezoneResolve";
import { format as formatDate } from "date-fns";
import { useDateLocale } from "@/lib/dateLocale";

interface BookingSummaryProps {
  originCity: string;
  destinationCity: string;
  date: string;
  departureTime?: string;
  arrivalTime?: string | null;
  /**
   * Fallback zone (the tenant's). Used only where a city has no backfilled
   * timezone of its own.
   */
  timezone?: string;
  /**
   * The BOARDING city's zone and the ALIGHTING city's zone (PR B3). The
   * passenger is committing money against these clocks, so each end must read
   * in the city they will actually be standing in — this summary rendered both
   * in the tenant's, which is an hour wrong for one end of a cross-border trip.
   */
  departureTimezone?: string | null;
  arrivalTimezone?: string | null;
  seats: string[];
  pricePerPassenger: number;
  tripType: "one-way" | "round-trip";
  discountAmount?: number;
  discountCode?: string;
}

/**
 * Live boarding-pass preview for the checkout right column.
 * Two regions separated by a dashed perforation — the upper "trip card"
 * and the lower "stub" with price + seats. Editorial type, hairline edges.
 */
export function BookingSummary({
  originCity,
  destinationCity,
  date,
  departureTime,
  arrivalTime,
  timezone = DEFAULT_TIMEZONE,
  departureTimezone,
  arrivalTimezone,
  seats,
  pricePerPassenger,
  tripType,
  discountAmount = 0,
  discountCode,
}: BookingSummaryProps) {
  const { t } = useTranslation();
  const dl = useDateLocale();
  const passengerCount = seats.length;
  const subtotal = pricePerPassenger * passengerCount;
  const total = Math.max(0, subtotal - discountAmount);

  const departZone = resolveStopTimezone(departureTimezone, null, timezone);
  const arriveZone = resolveStopTimezone(arrivalTimezone, departureTimezone, timezone);

  // The date shown must be the day the PASSENGER boards, in their boarding
  // city. `date` is the search day — the day the ROUTE departs — so on an
  // overnight leg it put a 3:00 AM boarding clock next to the previous day.
  //
  // Falling back to `date` as a plain yyyy-MM-dd label: round-tripping it
  // through a timezone (parse to a browser-local midnight, then render in
  // another zone) shifted it by a day for any browser east of that zone.
  let displayDate = date;
  try {
    if (departureTime) {
      displayDate = formatInTimezone(
        departureTime,
        departZone,
        dl.pattern("weekdayDateYearDot"),
        dl.locale,
      );
    } else {
      const [y, m, d] = date.split("-").map(Number);
      if (y && m && d) {
        // This is the SEARCH day — the day the route departs — not a boarding
        // date we resolved. For an untimed intermediate stop it can be the
        // previous day, so it is labelled rather than presented as the
        // passenger's own.
        displayDate = `${formatDate(new Date(y, m - 1, d), dl.pattern("weekdayDateYearDot"), { locale: dl.locale })} ${t("trips.routeDeparture", "(route departure)")}`;
      }
    }
  } catch {
    // keep raw
  }

  const formatTime = (timeStr: string | undefined | null, zone: string) => {
    if (!timeStr) return null;
    try {
      return formatInTimezone(timeStr, zone, "HH:mm");
    } catch {
      return null;
    }
  };

  const depart = formatTime(departureTime, departZone);
  const arrive = formatTime(arrivalTime, arriveZone);
  // Label the clocks only when the two ends sit in different zones.
  const crossesZones = departZone !== arriveZone;
  const departAbbr =
    crossesZones && departureTime
      ? getTimezoneAbbreviation(departZone, new Date(departureTime))
      : null;
  const arriveAbbr =
    crossesZones && arrivalTime
      ? getTimezoneAbbreviation(arriveZone, new Date(arrivalTime))
      : null;

  return (
    <article className="bg-white border hairline-strong shadow-ticket">
      {/* Stamp bar */}
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b hairline">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 bg-brand" aria-hidden="true" />
          <span className="text-eyebrow uppercase text-ink-muted">
            {t("checkout.summary.title")}
          </span>
        </div>
        {tripType === "round-trip" && (
          <span className="text-eyebrow uppercase text-brand border border-brand px-1.5 py-0.5">
            {t("search.roundTrip")}
          </span>
        )}
      </div>

      {/* Route headline */}
      <div className="px-5 pt-5 pb-4">
        <div className="text-eyebrow uppercase text-ink-subtle">
          {t("trips.departure")}
        </div>
        <div className="font-display text-[1.6rem] text-ink leading-tight mt-1 truncate">
          {originCity}
        </div>
        {/* Route line */}
        <div
          className="flex items-center gap-1 my-3"
          aria-hidden="true"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-ink flex-shrink-0" />
          <span className="flex-1 h-px bg-hairline-strong" />
          <span className="h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
        </div>
        <div className="text-eyebrow uppercase text-ink-subtle">
          {t("trips.arrival")}
        </div>
        <div className="font-display text-[1.6rem] text-ink leading-tight mt-1 truncate">
          {destinationCity}
        </div>
      </div>

      {/* Time / date chips */}
      <div className="px-5 pb-5 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <Field label={t("search.departureDate", "Date")} value={displayDate} />
        <Field
          label={t("trips.departure")}
          value={
            // Each end stands on its own: a known arrival must not disappear
            // because the boarding time is unknown (and vice versa).
            depart || arrive
              ? `${depart ? `${depart}${departAbbr ? ` ${departAbbr}` : ""}` : "—"}` +
                (arrive
                  ? ` — ${arrive}${arriveAbbr ? ` ${arriveAbbr}` : ""}`
                  : "")
              : "—"
          }
        />
        <Field
          label={t("search.passengers")}
          value={`${passengerCount}`}
        />
        <Field
          label={t("checkout.summary.seats")}
          value={seats
            .sort((a, b) => parseInt(a) - parseInt(b))
            .join(" · ")}
        />
      </div>

      {/* Perforation */}
      <div className="relative h-4 perforation" aria-hidden="true">
        {/* Left & right half-circle notches — pure CSS */}
        <span className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 h-4 w-4 rounded-full bg-stone-50 border-r hairline-strong" />
        <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 h-4 w-4 rounded-full bg-stone-50 border-l hairline-strong" />
      </div>

      {/* Stub — price block */}
      <div className="px-5 pt-4 pb-5">
        <div className="flex items-baseline justify-between text-sm tabular">
          <span className="text-ink-muted">
            ${pricePerPassenger.toFixed(0)} × {passengerCount}
          </span>
          <span className="text-ink">${subtotal.toFixed(2)}</span>
        </div>

        {discountAmount > 0 && (
          <div className="flex items-baseline justify-between text-sm tabular mt-1.5">
            <span className="text-ink-muted">
              {t("checkout.summary.discount")}
              {discountCode && ` · ${discountCode}`}
            </span>
            <span className="text-brand">−${discountAmount.toFixed(2)}</span>
          </div>
        )}

        <div className="flex items-baseline justify-between mt-3 pt-3 border-t hairline-strong">
          <span className="text-eyebrow uppercase text-ink-muted">
            {t("checkout.summary.total")}
          </span>
          <span className="font-display text-[1.75rem] text-ink tabular leading-none">
            ${total.toFixed(0)}
            <span className="text-[0.7rem] text-ink-muted ml-1.5 tracking-wide">
              USD
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-eyebrow uppercase text-ink-subtle">{label}</div>
      <div className="font-medium text-ink text-sm mt-0.5 tabular truncate">
        {value}
      </div>
    </div>
  );
}

export default BookingSummary;
